<template>
    <div class="python-editor">
        <div class="editor-container">
            <div class="line-numbers">
                <div class="line-number">1</div>
                <div v-for="n in lineCount" :key="n" class="line-number">{{ n + 1 }}</div>
                <div class="line-number">{{ lineCount + 2 }}</div>
            </div>
            <div class="code-content">
                <div class="code-header">
                    <span class="keyword">def</span> <span class="function"> run</span>(payload):
                </div>
                <div class="editor-wrapper">
                    <textarea
                        ref="editor"
                        v-model="script"
                        class="code-textarea"
                        spellcheck="false"
                        @input="handleInput"
                        @keydown.tab.prevent="insertTab"
                        @keydown.enter.prevent="handleEnter"
                        @scroll="syncScroll"
                        placeholder="    # Write your Python code here...">
                    </textarea>
                    <pre ref="highlight" class="code-highlight" v-html="highlightedCode"></pre>
                </div>
                <div class="code-footer">
                    <span class="keyword">return</span> output
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    name: 'WackScriptEditor',
    props: {
        value: {
            type: String,
            default: ''
        },
    },
    data() {
        return {
            script: this.value || '',
        }
    },
    computed: {
        lineCount() {
            return this.script.split('\n').length;
        },
        highlightedCode() {
            return this.highlightPython(this.script);
        }
    },
    watch: {
        value(newVal) {
            if (newVal !== this.script) {
                this.script = newVal || '';
            }
        }
    },
    methods: {
        handleInput() {
            this.$emit('input', this.script);
            this.syncScroll();
        },
        insertTab(event) {
            const textarea = event.target;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;

            // Insert four spaces at the cursor position
            this.script =
                this.script.substring(0, start) + "    " + this.script.substring(end);

            // Move the cursor after the inserted spaces
            this.$nextTick(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 4;
            });
        },
        handleEnter(event) {
            const textarea = event.target;
            const start = textarea.selectionStart;
            const lines = this.script.substring(0, start).split('\n');
            const currentLine = lines[lines.length - 1];

            // Calculate indentation of current line
            const indent = currentLine.match(/^\s*/)[0];

            // Check if line ends with colon (for if, for, def, etc.)
            const needsExtraIndent = currentLine.trim().endsWith(':');

            const newIndent = needsExtraIndent ? indent + '    ' : indent;

            // Insert newline with indentation
            this.script =
                this.script.substring(0, start) +
                '\n' + newIndent +
                this.script.substring(textarea.selectionEnd);

            this.$nextTick(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 1 + newIndent.length;
            });
        },
        syncScroll() {
            if (this.$refs.editor && this.$refs.highlight) {
                this.$refs.highlight.scrollTop = this.$refs.editor.scrollTop;
                this.$refs.highlight.scrollLeft = this.$refs.editor.scrollLeft;
            }
        },
        highlightPython(code) {
            if (!code) return '';

            // Escape HTML first
            let result = code
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');

            // Protect strings and comments first (they have priority)
            const stringMarkers = [];
            const commentMarkers = [];

            // Mark strings with placeholders
            result = result.replace(/(['"])(?:(?=(\\?))\2.)*?\1/g, (match) => {
                const id = `__STRING_${stringMarkers.length}__`;
                stringMarkers.push(match);
                return id;
            });

            // Mark comments with placeholders
            result = result.replace(/(#.*$)/gm, (match) => {
                const id = `__COMMENT_${commentMarkers.length}__`;
                commentMarkers.push(match);
                return id;
            });

            // Python keywords
            const keywords = ['def', 'class', 'if', 'elif', 'else', 'for', 'while', 'in', 'return',
                            'import', 'from', 'as', 'try', 'except', 'finally', 'with', 'lambda',
                            'pass', 'break', 'continue', 'and', 'or', 'not', 'is', 'None', 'True', 'False'];

            // Highlight keywords - use placeholders to avoid recursive replacement
            const keywordMarkers = [];
            keywords.forEach(keyword => {
                const regex = new RegExp(`\\b${keyword}\\b`, 'g');
                result = result.replace(regex, (match) => {
                    const id = `__KEYWORD_${keywordMarkers.length}__`;
                    keywordMarkers.push(match);
                    return id;
                });
            });

            // Restore keywords with highlighting
            keywordMarkers.forEach((keyword, i) => {
                result = result.replace(`__KEYWORD_${i}__`, `<span class="keyword">${keyword}</span>`);
            });

            // Highlight numbers
            result = result.replace(/\b(\d+\.?\d*)\b/g, '<span class="number">$1</span>');

            // Highlight function calls
            result = result.replace(/\b([a-zA-Z_]\w*)\s*(?=\()/g, '<span class="function-call">$1</span>');

            // Restore strings with highlighting
            stringMarkers.forEach((str, i) => {
                result = result.replace(`__STRING_${i}__`, `<span class="string">${str}</span>`);
            });

            // Restore comments with highlighting
            commentMarkers.forEach((comment, i) => {
                result = result.replace(`__COMMENT_${i}__`, `<span class="comment">${comment}</span>`);
            });

            return result;
        }
    },
}
</script>

<style scoped>
.python-editor {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
    font-size: 14px;
}

.editor-container {
    display: flex;
    border: 1px solid #3c3c3c;
    border-radius: 4px;
    background: #1e1e1e;
    overflow: hidden;
    min-height: 300px;
}

/* Line Numbers */
.line-numbers {
    background: #1e1e1e;
    color: #858585;
    padding: 8px;
    text-align: right;
    user-select: none;
    min-width: 50px;
    border-right: 1px solid #3c3c3c;
}

.line-number {
    line-height: 21px;
    font-size: 13px;
    padding: 0 8px;
}

/* Code Content */
.code-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #1e1e1e;
}

.code-header,
.code-footer {
    padding: 8px 16px;
    color: #d4d4d4;
    line-height: 21px;
    background: #1e1e1e;
    border-bottom: 1px solid #3c3c3c;
}

.code-header {
    padding-left: 4px;
    background: #252526;
    font-size: 13px;
    opacity: 0.8;
}

.code-footer {
    border-bottom: none;
    border-top: 1px solid #3c3c3c;
    background: #252526;
    font-size: 13px;
    opacity: 0.8;
}

/* Editor Wrapper */
.editor-wrapper {
    flex: 1;
    position: relative;
    overflow: hidden;
}

.code-textarea {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 8px 16px;
    margin: 0;
    border: none;
    outline: none;
    background: transparent;
    color: transparent;
    caret-color: #fff;
    font-family: inherit;
    font-size: 14px;
    line-height: 21px;
    resize: none;
    white-space: pre;
    overflow-wrap: normal;
    overflow: auto;
    z-index: 2;
    tab-size: 4;
}

.code-highlight {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 8px 16px;
    margin: 0;
    background: transparent;
    color: #d4d4d4;
    font-family: inherit;
    font-size: 14px;
    line-height: 21px;
    white-space: pre;
    overflow-wrap: normal;
    overflow: auto;
    pointer-events: none;
    z-index: 1;
}

/* Syntax highlighting colors (VS Code Dark+ theme) */
.keyword {
    color: #569cd6;
    font-weight: 600;
}

.function {
    color: #dcdcaa;
}

.code-highlight >>> .keyword {
    color: #569cd6;
    font-weight: 600;
}

.code-highlight >>> .function {
    color: #dcdcaa;
}

.code-highlight >>> .function-call {
    color: #dcdcaa;
}

.code-highlight >>> .string {
    color: #ce9178;
}

.code-highlight >>> .number {
    color: #b5cea8;
}

.code-highlight >>> .comment {
    color: #6a9955;
    font-style: italic;
}

/* Scrollbar styling */
.code-textarea::-webkit-scrollbar,
.code-highlight::-webkit-scrollbar {
    width: 10px;
    height: 10px;
}

.code-textarea::-webkit-scrollbar-track,
.code-highlight::-webkit-scrollbar-track {
    background: #1e1e1e;
}

.code-textarea::-webkit-scrollbar-thumb,
.code-highlight::-webkit-scrollbar-thumb {
    background: #424242;
    border-radius: 5px;
}

.code-textarea::-webkit-scrollbar-thumb:hover,
.code-highlight::-webkit-scrollbar-thumb:hover {
    background: #4e4e4e;
}

/* Placeholder styling */
.code-textarea::placeholder {
    color: #6a6a6a;
}
</style>